// @ts-nocheck - React 18 type incompatibilities with custom components
import React, { useState } from 'react';
import { Icon20Check, Icon24ChevronDown, Icon24ChevronRight } from '@vkontakte/icons';
import clsx from 'clsx';
import Text from '@/components/Text';
import ProgressCircle from '@/components/ProgressCircle';
import Loader from '@/components/Loader';

interface Lesson {
  title: string;
  description: string;
  hookCta?: string;
  completed?: boolean;
  completedAt?: string;
  attempts?: number;
  loading?: boolean;
  pregenerated?: boolean;
}

interface ChapterAccordionProps {
  chapterNumber: number;
  chapterTitle: string;
  lessons: Lesson[];
  isExpanded: boolean;
  onToggle: () => void;
  onStartLesson: (lessonIndex: number) => void;
  completedLessonsCount: number;
  isLoading?: boolean;
}

const SkeletonLesson = () => (
  <div className="py-4 border-b border-white/5 last:border-0">
    <div className="flex items-center justify-between gap-3 animate-pulse">
      <div className="h-4 bg-white/10 rounded w-2/3"></div>
      <div className="w-5 h-5 bg-white/10 rounded"></div>
    </div>
  </div>
);

const CHAPTER_BACKGROUNDS = [
  'https://cdn.bloomapp.com/learn/investing-next-bg-2.png',
  'https://cdn.bloomapp.com/learn/picking-next-bg.png',
  'https://cdn.bloomapp.com/learn/anal-next-bg.png',
  'https://cdn.bloomapp.com/learn/finance-next-bg.png',
  'https://cdn.bloomapp.com/learn/crypto-next-bg.png',
  'https://cdn.bloomapp.com/learn/company-next-bg.png',
  'https://cdn.bloomapp.com/learn/market-next-bg.png',
  'https://cdn.bloomapp.com/learn/assets-next-bg.png',
  'https://cdn.bloomapp.com/learn/stats-next-bg-2.png',
  'https://cdn.bloomapp.com/learn/pro-next-bg-2.png',
];

const ChapterAccordion: React.FC<ChapterAccordionProps> = ({
  chapterNumber,
  chapterTitle,
  lessons,
  isExpanded,
  onToggle,
  onStartLesson,
  completedLessonsCount,
  isLoading = false,
}) => {
  const totalLessons = lessons.length;
  const progressPercentage = totalLessons > 0 ? (completedLessonsCount / totalLessons) * 100 : 0;
  const isAllCompleted = completedLessonsCount === totalLessons && totalLessons > 0;

  const bgImage = CHAPTER_BACKGROUNDS[(chapterNumber - 1) % CHAPTER_BACKGROUNDS.length];

  return (
    <div className="mb-4">
      <div className={clsx(
        "rounded-2xl overflow-hidden backdrop-blur-sm transition-all relative min-h-[80px]",
        "border border-white/5 shadow-xl"
      )}>
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{ 
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
          }} 
        />
        
        <button
          onClick={onToggle}
          className="w-full flex items-center p-5 text-left hover:bg-white/5 transition-colors relative z-10"
        >
          {/* Chapter Number */}
          <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center mr-4 shrink-0 bg-white/10">
            <span className="text-white text-sm">{chapterNumber}</span>
          </div>

          {/* Chapter Title */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-heading-md leading-tight truncate">
              {chapterTitle || 'Untitled Chapter'}
            </h3>
            <div className="mt-1 flex items-center gap-2">
              {isLoading ? (
                <Text size="xs" className="text-white/40">Generating lessons...</Text>
              ) : (
                <Text size="xs" className="text-white/60">
                  {completedLessonsCount}/{totalLessons} lessons
                </Text>
              )}
            </div>
          </div>

          {/* Progress / Status */}
          <div className="ml-4 flex items-center gap-3">
            {isAllCompleted ? (
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                <Icon20Check className="text-white" />
              </div>
            ) : (
              <ProgressCircle 
                progress={progressPercentage} 
                size={32} 
                strokeWidth={3}
                circleColor="rgba(255,255,255,0.15)"
                progressColor="#FFF"
              />
            )}
            <Icon24ChevronDown 
              className={clsx(
                "text-white/40 transition-transform duration-300",
                isExpanded && "rotate-180"
              )} 
            />
          </div>
        </button>

        {/* Lessons List */}
        <div 
          className={clsx(
            "overflow-hidden transition-all duration-300 ease-in-out relative z-10",
            isExpanded ? "max-h-[1000px] border-t border-white/5" : "max-h-0"
          )}
        >
          <div className="py-2 relative z-10">
            {isLoading ? (
              <>
                <SkeletonLesson />
                <SkeletonLesson />
                <SkeletonLesson />
              </>
            ) : (
              lessons.map((lesson, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartLesson(index);
                  }}
                  className="w-full py-4 border-b border-white/5 last:border-0 flex items-center justify-between gap-3 text-left hover:bg-white/5 px-5 rounded-lg transition-all group min-w-0 relative z-20 cursor-pointer"
                >
                    <Text className="text-white flex-1 leading-snug text-sm md:text-base line-clamp-2">
                      {lesson.title || 'Lesson'}
                    </Text>

                  <div className="shrink-0">
                    {lesson.loading ? (
                      <Loader color="white" className="h-4 w-4" />
                    ) : lesson.completed ? (
                      <Icon20Check className="text-white" />
                    ) : (
                      <Icon24ChevronRight className="text-white/40 group-hover:translate-x-1 transition-transform" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterAccordion;
